<template>
    <div class="coa-fields" :class="{ 'coa-fields--compact': compact }">
        <b-row>
            <b-col :cols="fieldCols">
                <b-form-group label="Assay %" :label-size="inputSize" class="mb-2">
                    <b-input type="number" step="0.01" min="0" :size="inputSize" :value="assayPercent"
                        :readonly="readonly" placeholder="e.g. 99.7" @input="onAssayInput" />
                </b-form-group>
            </b-col>
            <b-col :cols="fieldCols">
                <b-form-group label="Assay Basis" :label-size="inputSize" class="mb-2">
                    <b-form-select :size="inputSize" :value="assayBasis" :options="basisOptions" :disabled="readonly"
                        @input="onBasisInput" />
                </b-form-group>
            </b-col>
            <b-col :cols="fieldCols">
                <b-form-group :label-size="inputSize" class="mb-2">
                    <template #label>
                        LOD / Water Content %
                        <small v-if="!lodApplies" class="text-muted">(not applicable As-Is)</small>
                    </template>
                    <b-input type="number" step="0.01" min="0" :size="inputSize" :value="lodPercent"
                        :readonly="readonly" :disabled="!lodApplies" :placeholder="lodApplies ? 'e.g. 3.1' : ''"
                        @input="onLodInput" />
                </b-form-group>
            </b-col>
            <b-col :cols="fieldCols" class="coa-fields__preview">
                <b-form-group v-if="compact" label="Activity Factor" :label-size="inputSize" class="mb-2">
                    <b-input :size="inputSize" :value="factorText" readonly plaintext class="font-weight-bold" />
                </b-form-group>
                <div v-else class="mb-2">
                    Activity factor: <strong>{{ factorText || '—' }}</strong>
                    <small v-if="preview.hasCoa && !preview.unusable" class="text-muted">
                        (assay ÷ 100<span v-if="lodApplies"> × (100 − LOD) ÷ 100</span>)
                    </small>
                </div>
            </b-col>
        </b-row>

        <div v-if="siblingPrefilled" class="small text-info mb-1">
            <b-icon icon="info-circle" /> COA pre-filled from the same lot{{ siblingLocationText }}.
        </div>
        <div v-if="siblingMismatch" class="small text-warning mb-1">
            <b-icon icon="exclamation-triangle-fill" />
            These values differ from the same lot{{ siblingLocationText }}
            (assay {{ sibling.assayPercent }}% {{ basisLabel(sibling.assayBasis) }}<template v-if="sibling.lodPercent != null">, LOD {{ sibling.lodPercent }}%</template>).
            The same physical lot should carry the same COA.
        </div>
        <div v-for="(warning, index) in warnings" :key="index" class="small text-warning mb-1">
            <b-icon icon="exclamation-triangle-fill" /> {{ warning }}
        </div>
        <div v-if="fixedFactorNotice" class="small text-muted mb-1">
            <b-icon icon="info-circle" /> {{ fixedFactorNotice }}
        </div>
    </div>
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator';
import { ASSAY_BASIS_LABELS, ASSAY_BASIS_OPTIONS, AssayBasis, FixedFactorIngredientUsage, LotCoaSibling } from '@/models/Inventory';
import {
    CoaFactorPreview,
    CoaRangeBounds,
    UNBOUNDED_COA_RANGE,
    assayBasisAppliesLod,
    coaDiffersFromSibling,
    describeCoaRangeWarnings,
    describeUnusableCoa,
    formatActivityFactor,
    previewCoaFactor,
    toNumberOrNull,
} from '@/util/activityFactor';

/**
 * Certificate of Analysis entry for one inventory lot: assay %, assay basis and LOD / water content,
 * with a live activity-factor preview and the warnings the server will apply. Shared by the lot edit
 * modal and the receiving lot lines so both forms show the same fields, the same factor and the same
 * warnings. Bound with `.sync` on the three values; the host owns fetching the sibling row and the
 * fixed-factor usages, this component only explains them.
 */
@Component({ name: 'CoaFieldsComponent' })
export default class CoaFieldsComponent extends Vue {
    @Prop({ default: null }) readonly assayPercent!: number | null;
    @Prop({ default: null }) readonly assayBasis!: AssayBasis | null;
    @Prop({ default: null }) readonly lodPercent!: number | null;
    @Prop({ default: () => UNBOUNDED_COA_RANGE }) readonly bounds!: CoaRangeBounds;
    @Prop({ default: 4 }) readonly decimalPlaces!: number;
    @Prop({ default: false }) readonly readonly!: boolean;
    /** Table-row layout: small controls on one line, factor shown as a fourth field. */
    @Prop({ default: false }) readonly compact!: boolean;
    /** Another inventory row for the same lot carrying COA; drives the pre-fill note and the mismatch warning. */
    @Prop({ default: null }) readonly sibling!: LotCoaSibling | null;
    /** True when the host copied the sibling's values into the fields, so the user is told where they came from. */
    @Prop({ default: false }) readonly siblingPrefilled!: boolean;
    /** Formulas in which this drug is a flagged ingredient with a fixed factor (COA recorded but not applied). */
    @Prop({ default: () => [] }) readonly fixedFactorUsages!: FixedFactorIngredientUsage[];

    protected readonly basisOptions = ASSAY_BASIS_OPTIONS;

    get inputSize(): 'sm' | undefined {
        return this.compact ? 'sm' : undefined;
    }

    /** Four small fields across one table row when compact; stacked full-width fields in the modal. */
    get fieldCols(): number {
        return this.compact ? 3 : 12;
    }

    get lodApplies(): boolean {
        return assayBasisAppliesLod(this.assayBasis);
    }

    get preview(): CoaFactorPreview {
        return previewCoaFactor(
            { assayPercent: this.assayPercent, assayBasis: this.assayBasis, lodPercent: this.lodPercent },
            this.bounds);
    }

    get factorText(): string {
        return formatActivityFactor(this.preview.factor, this.decimalPlaces);
    }

    /** Everything the server would flag about these values, in the order it matters. */
    get warnings(): string[] {
        const warnings: string[] = [];
        if (this.preview.unusableReason) {
            warnings.push(describeUnusableCoa(this.preview.unusableReason, this.bounds));
        }
        if (this.preview.lodAssumedZero) {
            warnings.push('LOD / water content is blank, so it will be treated as 0% for this basis.');
        }
        warnings.push(...describeCoaRangeWarnings(this.preview.rangeWarnings, this.bounds));
        return warnings;
    }

    get siblingMismatch(): boolean {
        if (!this.sibling) return false;
        return coaDiffersFromSibling(
            { assayPercent: this.assayPercent, assayBasis: this.assayBasis, lodPercent: this.lodPercent },
            this.sibling);
    }

    get siblingLocationText(): string {
        return this.sibling?.storeName ? ` at ${this.sibling.storeName}` : '';
    }

    get fixedFactorNotice(): string | null {
        if (!this.fixedFactorUsages?.length) return null;
        const names = this.fixedFactorUsages
            .map(u => `${u.compoundName} (fixed factor ${u.fixedConversionFactor})`)
            .join(', ');
        return `COA entered here is recorded but will not drive the weigh-out for: ${names} — those formulas use the ingredient's fixed conversion factor instead.`;
    }

    basisLabel(basis: string | null | undefined): string {
        return basis && basis in ASSAY_BASIS_LABELS ? ASSAY_BASIS_LABELS[basis as AssayBasis] : '';
    }

    onAssayInput(value: string | number | null) {
        this.$emit('update:assayPercent', toNumberOrNull(value));
    }

    onBasisInput(value: AssayBasis | null) {
        this.$emit('update:assayBasis', value || null);
    }

    onLodInput(value: string | number | null) {
        this.$emit('update:lodPercent', toNumberOrNull(value));
    }
}
</script>

<style scoped>
.coa-fields--compact ::v-deep(legend),
.coa-fields--compact ::v-deep(label) {
    padding-bottom: 0;
    margin-bottom: 0;
    font-size: .85em;
}

.coa-fields--compact ::v-deep(.form-control-plaintext) {
    padding-left: 0;
}
</style>
